import React, { useMemo } from 'react';
import { WithTranslationsProps } from 'react-utilities';
import HomePageCarouselDiscoveryApi from '../homePage/discoveryApi/HomePageCarouselDiscoveryApi';
import useFriendsPresence from '../common/hooks/useFriendsPresence';
import { TGameData } from '../common/types/bedev1Types';
import { TGameSort } from '../common/types/bedev2Types';
import { useContentMetadata } from './utils/contentMetadataContextProvider';
import { getNumCarouselTiles } from '../common/components/GameTileUtils';
import { getHydratedGameData } from './utils/gameSortUtils';
import GamesPageGameCarousel from '../gamesPage/components/GamesPageGameCarousel';
import { PageContext } from '../common/types/pageContext';

type THomePageDiscoveryApiProps = {
  translate: WithTranslationsProps['translate'];
  sort: TGameSort;
  positionId: number;
  page: PageContext.HomePage | PageContext.GamesPage;
  itemsPerRow: number | undefined;
  startingRow: number | undefined;
  loadMoreGames?: () => void;
  isLoadingMoreGames?: boolean;
  isExpandHomeContentEnabled?: boolean;
  isChartsPageRenameEnabled?: boolean;
};

export const GameCarouselFeedItem = ({
  translate,
  sort,
  positionId,
  page,
  itemsPerRow,
  startingRow,
  loadMoreGames,
  isLoadingMoreGames,
  isExpandHomeContentEnabled,
  isChartsPageRenameEnabled
}: THomePageDiscoveryApiProps): JSX.Element | null => {
  const friendsPresence = useFriendsPresence();

  const { contentMetadata } = useContentMetadata();

  const carouselData: TGameData[] = useMemo(() => {
    if (isExpandHomeContentEnabled) {
      return getHydratedGameData(sort, contentMetadata).slice(0, itemsPerRow);
    }

    return getHydratedGameData(sort, contentMetadata).slice(
      0,
      getNumCarouselTiles(page, sort.topicLayoutData?.componentType)
    );
  }, [sort, contentMetadata, page, itemsPerRow, isExpandHomeContentEnabled]);

  if (carouselData?.length === 0) {
    return null;
  }

  if (page === PageContext.GamesPage) {
    return (
      <GamesPageGameCarousel
        key={sort.topic}
        sort={sort}
        translate={translate}
        positionId={positionId}
        page={page}
        gameData={carouselData}
        loadMoreGames={loadMoreGames as () => void}
        isLoadingMoreGames={isLoadingMoreGames === true}
        tooltipInfoText={sort.topicLayoutData?.infoText}
        hideSeeAll={sort.topicLayoutData?.hideSeeAll === 'true'}
        componentType={sort.topicLayoutData?.componentType}
        playerCountStyle={sort.topicLayoutData?.playerCountStyle}
        playButtonStyle={sort.topicLayoutData?.playButtonStyle}
        itemsPerRow={itemsPerRow}
        isChartsPageRenameEnabled={isChartsPageRenameEnabled}
      />
    );
  }

  return (
    <HomePageCarouselDiscoveryApi
      key={sort.topic}
      sort={sort}
      translate={translate}
      positionId={positionId}
      gameData={carouselData}
      friendsPresence={friendsPresence}
      itemsPerRow={itemsPerRow}
      startingRow={startingRow}
      componentType={sort.topicLayoutData?.componentType}
      playerCountStyle={sort.topicLayoutData?.playerCountStyle}
      playButtonStyle={sort.topicLayoutData?.playButtonStyle}
      hoverStyle={sort.topicLayoutData?.hoverStyle}
      tooltipInfoText={sort.topicLayoutData?.infoText}
      hideSeeAll={sort.topicLayoutData?.hideSeeAll === 'true'}
      navigationRootPlaceId={sort.topicLayoutData?.navigationRootPlaceId}
      isSponsoredFooterAllowed={sort.topicLayoutData?.isSponsoredFooterAllowed === 'true'}
      seeAllLinkPath={sort.topicLayoutData?.linkPath}
      endTimestamp={sort.topicLayoutData?.endTimestamp}
      countdownString={sort.topicLayoutData?.countdownString}
      isExpandHomeContentEnabled={isExpandHomeContentEnabled}
    />
  );
};

GameCarouselFeedItem.defaultProps = {
  loadMoreGames: undefined,
  isLoadingMoreGames: undefined,
  isExpandHomeContentEnabled: undefined,
  isChartsPageRenameEnabled: undefined
};

export default GameCarouselFeedItem;
